import { ZAttribute } from "src/types/ZAttribute";
import { ZDisplayId } from "src/types/ZDisplayId";
import { ZGroup } from "src/types/ZGroup";
import { ZIdName } from "src/types/ZIdName";
import { ZUid } from "src/types/zUid";

/**
 * Доменный узел дерева объектов.
 *
 * Содержит только данные. Заголовок, иконка, признак листа и права здесь не
 * хранятся: их даёт проекция (`view/projectNode.ts`) через реестр вариантов.
 * Разделение нужно, чтобы поведение узла задавалось одной таблицей, а не
 * шестью switch-блоками, как в v1.
 *
 * Дети тоже не хранятся в узле: связь «владелец → дети» держит TreeStore.
 * Благодаря этому «дети не загружены» и «детей нет» — разные состояния карты,
 * а не соглашение про `undefined` внутри узла.
 */
export type NodeId = string;

/**
 * Идентификатор ещё не сохранённой сущности.
 * Значение совпадает с `newItemId` из v1: это одно и то же соглашение с
 * сервером, а не два разных.
 */
export const unsavedId = -1;

/**
 * Группа без вложений: дети живут в TreeStore, а не внутри данных.
 * Тип выводится из общего ZGroup, а не импортируется из v1: домен не должен
 * зависеть от модулей старой вкладки.
 */
export type GroupData = Omit<ZGroup, "attributes" | "groups">;

/**
 * Данные объекта в дереве — то, что приходит из каталога `/v3/objects`.
 * Полный ObjectDto живёт в отдельном ресурсе и в узел не копируется.
 *
 * displayId допускает null: у объекта, созданного локально, его ещё нет,
 * а подставлять фиктивный нельзя — он виден пользователю в заголовке.
 */
export type ObjectNodeData = {
  id: ZUid;
  displayId: ZDisplayId | null;
  name: string;
  canUpdate: boolean;
  canDelete: boolean;
};

export type ObjectNode = {
  kind: "object";
  id: NodeId;
  data: ObjectNodeData;
};

export type GroupNode = {
  kind: "group";
  id: NodeId;
  ownerId: NodeId;
  data: GroupData;
};

export type AttributeNode = {
  kind: "attribute";
  id: NodeId;
  ownerId: NodeId;
  data: ZAttribute;
};

export type ValueNode = {
  kind: "value";
  id: NodeId;
  ownerId: NodeId;
  data: ZIdName;
};

/**
 * Объект не имеет владельца по типу, а не по соглашению: объекты — всегда корни.
 * Это единственная неоднородность союза, и она содержательна.
 */
export type TreeNode = AttributeNode | GroupNode | ObjectNode | ValueNode;

export type NodeKind = TreeNode["kind"];

/**
 * Идентификаторы.
 *
 * Один и тот же атрибут показывается в нескольких местах дерева (объект,
 * мнемоническая группа, значение справочника), поэтому в его идентификатор
 * входит владелец. Группы уникальны сами по себе.
 *
 * Формат приватен для v3: ни одна форма ключи не строит и не разбирает
 * (проверено по коду), поэтому совпадение с v1 не требуется.
 */
export const objectNodeId = (objectId: ZUid): NodeId => `obj:${objectId}`;

export const groupNodeId = (groupId: ZUid): NodeId => `group:${groupId}`;

export const attributeNodeId = (attrId: ZUid, ownerId: NodeId): NodeId =>
  `attr:${attrId}@${ownerId}`;

export const valueNodeId = (valueId: ZUid, ownerId: NodeId): NodeId =>
  `value:${valueId}@${ownerId}`;

export const objectNode = (data: ObjectNodeData): ObjectNode => ({
  kind: "object",
  id: objectNodeId(data.id),
  data,
});

export const groupNode = (data: GroupData, ownerId: NodeId): GroupNode => ({
  kind: "group",
  id: groupNodeId(data.id),
  ownerId,
  data,
});

export const attributeNode = (
  data: ZAttribute,
  ownerId: NodeId,
): AttributeNode => ({
  kind: "attribute",
  id: attributeNodeId(data.id, ownerId),
  ownerId,
  data,
});

export const valueNode = (data: ZIdName, ownerId: NodeId): ValueNode => ({
  kind: "value",
  id: valueNodeId(data.id, ownerId),
  ownerId,
  data,
});

export const isObjectNode = (node: TreeNode): node is ObjectNode =>
  node.kind === "object";

/**
 * Владелец есть у всех вариантов, кроме объекта.
 * Отдельная функция, потому что у `ObjectNode` поля `ownerId` нет вовсе —
 * читать его через `"ownerId" in node` по коду не нужно.
 */
export const ownerIdOf = (node: TreeNode): NodeId | null =>
  node.kind === "object" ? null : node.ownerId;
